import { useState } from "react";
import { Button, Tooltip } from "flowbite-react";
import { HiBan, HiEye, HiPencil, HiRefresh, HiTrash } from "react-icons/hi";
import { useNavigate } from "react-router-dom";
import { WorkShift, DataGridPagination } from "@/sharedTypes";
import DataTable from "@/components/Table";
import ConfirmationDialog from "@/components/ConfirmationDialog";
import {
  useDeleteWorkShiftMutation,
  useHardDeleteWorkShiftMutation,
  useRestoreWorkShiftMutation,
  useUpdateWorkShiftMutation,
} from "@/api/agentApi/workShift";
import { getErrorMessage } from "@/utils/requestUtils";
import { toast } from "@/lib/toast";
import { useTranslation } from "react-i18next";
import WorkShiftFormDialog from "./WorkShiftFormDialog";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { isCompanyAdminUser } from "@/constants/agent";

interface WorkShiftListTableProps {
  data: WorkShift[];
  pagination: DataGridPagination;
  onPageChange: (page: number) => void;
  showInactive: boolean;
}

export default function WorkShiftListTable({
  data,
  pagination,
  onPageChange,
  showInactive,
}: WorkShiftListTableProps) {
  const { t } = useTranslation(["workShift", "common"]);
  const user = useCurrentUser();
  const isAdmin = isCompanyAdminUser(user);
  const navigate = useNavigate();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedShift, setSelectedShift] = useState<WorkShift | null>(null);
  const [confirmDialog, setConfirmDialog] = useState<{
    title: string;
    body: string;
    action: "disable" | "restore" | "hardDelete";
    onConfirm: () => void;
  } | null>(null);

  const [updateWorkShift] = useUpdateWorkShiftMutation();
  const [deleteWorkShift] = useDeleteWorkShiftMutation();
  const [restoreWorkShift] = useRestoreWorkShiftMutation();
  const [hardDeleteWorkShift] = useHardDeleteWorkShiftMutation();

  const handleEdit = (shift: WorkShift) => {
    setSelectedShift(shift);
    setIsEditModalOpen(true);
  };

  const handleEditSubmit = (updated: any) => {
    if (!selectedShift?.id) return;
    
    // Remove id and other read-only fields from the payload
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { id, createdAt, updatedAt, company_id, ...payload } = updated;

    updateWorkShift({
      id: selectedShift.id,
      data: payload,
    })
      .unwrap()
      .then(() => {
        toast.success(t("workShift:updateSuccess"));
        setIsEditModalOpen(false);
      })
      .catch((err) => toast.error(getErrorMessage(err)));
  };

  const handleDisable = (id: number) => {
    setConfirmDialog({
      title: t("workShift:disableConfirmTitle"),
      body: t("workShift:disableConfirmBody"),
      action: "disable",
      onConfirm: () => {
        deleteWorkShift(id)
          .unwrap()
          .then(() => {
            toast.success(t("workShift:disableSuccess"));
            setConfirmDialog(null);
          })
          .catch((err) => toast.error(getErrorMessage(err)));
      },
    });
  };

  const handleRestore = (id: number) => {
    setConfirmDialog({
      title: t("workShift:restoreConfirmTitle"),
      body: t("workShift:restoreConfirmBody"),
      action: "restore",
      onConfirm: () => {
        restoreWorkShift(id)
          .unwrap()
          .then(() => {
            toast.success(t("workShift:restoreSuccess"));
            setConfirmDialog(null);
          })
          .catch((err) => toast.error(getErrorMessage(err)));
      },
    });
  };

  const handleHardDelete = (id: number) => {
    setConfirmDialog({
      title: t("workShift:deleteConfirmTitle"),
      body: t("workShift:deleteConfirmBody"),
      action: "hardDelete",
      onConfirm: () => {
        hardDeleteWorkShift(id)
          .unwrap()
          .then(() => {
            toast.success(t("workShift:deleteSuccess"));
            setConfirmDialog(null);
          })
          .catch((err) => toast.error(getErrorMessage(err)));
      },
    });
  };

  const columns = [
    { key: "shift_description", label: t("workShift:description") },
    { key: "shift_hours_start", label: t("workShift:startTime") },
    { key: "shift_hours_end", label: t("workShift:endTime") },
    { key: "break_time_start", label: t("workShift:breakStartTime") },
    { key: "break_time_end", label: t("workShift:breakEndTime") },
    ...(isAdmin
      ? [
          {
            key: "actions",
            label: t("workShift:actions"),
            render: (row: WorkShift) => (
              <div className="flex gap-2">
                <Tooltip content={t("common:view")}>
                  <Button
                    size="xs"
                    color="light"
                    onClick={() => navigate(`/work-shifts/${row.id}`)}
                  >
                    <HiEye className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                  </Button>
                </Tooltip>
                {showInactive ? (
                  <>
                    <Tooltip content={t("common:restore")}>
                      <Button
                        size="xs"
                        color="light"
                        onClick={() => handleRestore(row.id)}
                      >
                        <HiRefresh className="h-4 w-4 text-green-600 dark:text-green-400" />
                      </Button>
                    </Tooltip>
                    <Tooltip content={t("common:delete")}>
                      <Button
                        size="xs"
                        color="light"
                        onClick={() => handleHardDelete(row.id)}
                      >
                        <HiTrash className="h-4 w-4 text-red-600 dark:text-red-400" />
                      </Button>
                    </Tooltip>
                  </>
                ) : (
                  <>
                    <Tooltip content={t("common:edit")}>
                      <Button
                        size="xs"
                        color="light"
                        onClick={() => handleEdit(row)}
                      >
                        <HiPencil className="h-4 w-4 text-purple-600 dark:text-purple-400" />
                      </Button>
                    </Tooltip>
                    <Tooltip content={t("common:disable")}>
                      <Button
                        size="xs"
                        color="light"
                        onClick={() => handleDisable(row.id)}
                      >
                        <HiBan className="h-4 w-4 text-orange-600 dark:text-orange-400" />
                      </Button>
                    </Tooltip>
                  </>
                )}
              </div>
            ),
          },
        ]
      : []),
  ];

  return (
    <>
      <DataTable
        columns={columns}
        data={data}
        pagination={pagination}
        onPageChange={onPageChange}
      />

      {isEditModalOpen && (
        <WorkShiftFormDialog
          open={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          onSubmit={handleEditSubmit}
          mode="edit"
          initialData={selectedShift}
        />
      )}
      
      {confirmDialog && (
          <ConfirmationDialog
             onCancel={() => setConfirmDialog(null)}
             onConfirm={confirmDialog.onConfirm}
             title={confirmDialog.title}
             body={confirmDialog.body}
          />
      )}
    </>
  );
}
